import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Notepad16Regular } from "@fluentui/react-icons";
import {
    calendarTerminService,
    type CalendarTerminNoteDto,
    type TerminMailDto,
} from "../../services/calendarTerminService";
import { documentService, type DocumentDto } from "../../services/documentService";
import { vorgangService } from "../../services/vorgangService";
import type { VorgangCreateResult } from "../vorgang/create/VorgangCreateFlowWindow";
import {
    NeuerVorgangAusTerminDialog,
    VorgangZuweisenDialog,
    type TerminVorgangKontext,
} from "./TerminVorgangDialog";
import type { TerminTeilnehmerQuelle } from "./terminVorgang";
import { emailAusTerminMail, terminMailZeile } from "./terminMails";
import { useWindowManager } from "../windowManager/WindowManagerContext";
import { openMailDetailWindow } from "../mail/MailDetailWindow";
import { hinweisen } from "../redesign/bestaetigen";

/**
 * Lokale App-Logik zu einem Outlook-Termin (Sidecar): Notizen mit @Mention,
 * Dokumente, Vorgang-Verknüpfung und — seit P10 — die mit dem Termin
 * verknüpften MAILS. Identifiziert über (mailbox, eventId); der Sidecar wird
 * server-seitig bei der ersten Notiz/Verknüpfung/Datei lazy angelegt. Für den
 * Dokument-Upload brauchen wir aber vorab die terminId, die wir per getMeta
 * (get-or-create) auflösen.
 *
 * P10: Die Mail-Liste ist reine ANZEIGE aus dem MailLink-Snapshot — kein
 * Graph-Abruf, deshalb bleibt sie auch bei verschobenen oder gelöschten
 * Nachrichten lesbar. Verknüpft wird an anderer Stelle (Mail-Leiste bzw. der
 * Vorgang); hier gibt es nur das Öffnen und das Lösen. Was der Server liefert,
 * ist je Mail sichtbarkeitsgefiltert — die Liste kann also kürzer sein als das,
 * was tatsächlich am Termin hängt.
 */
type Props = {
    mailbox: string;
    eventId: string;
    /**
     * Kein SCHREIBrecht am Graph-Kalender → Notizen und Dokumente sind
     * gesperrt. Die beiden hängen bewusst hier und NICHT an `kmvsReadOnly`:
     * ihre Endpoints (`POST …/meta` für den Upload, `POST …/notes`) verlangen
     * weiterhin `assertCalendarWrite`. Hingen sie am gelockerten Gate, wären
     * Notizfeld und „+ Datei hochladen" auf einem CBB-/Lesekalender aktiv und
     * das Backend antwortete 403 — genau die stille Fehl-Aktion, die K3
     * abschafft.
     */
    graphReadOnly?: boolean;
    /**
     * Sperrt AUSSCHLIESSLICH den Vorgangs-Block (verknüpfen/lösen, PATCH
     * …/vorgang). E10 (PO 22.08.2026) deckt genau das ab: die Verknüpfung
     * schreibt nur KMVS-Metadaten am Sidecar und genügt sich mit Leserecht am
     * Kalender. Default false.
     */
    kmvsReadOnly?: boolean;
    /**
     * Teilnehmer/Organisator des Termins (SP8/A17): Basis der Kontakt-
     * Ableitung bei „+ Neuen Vorgang erstellen". Ohne Kontext öffnet der
     * Dialog den Erstell-Flow direkt (Kontakt-Wahl in Schritt 1).
     */
    teilnehmerKontext?: TerminTeilnehmerQuelle;
    /**
     * Fenster-ID des Termin-Fensters (WindowManager). Der Sprung auf den
     * verknüpften Vorgang navigiert die Seite HINTER dem Fenster — ohne diese
     * ID bliebe das Fenster darüber stehen bzw. löste bei einem maximierten
     * Fenster die Rückfrage des WindowNavigationGuard aus. Mit ID wandert es
     * vorher ins Dock, derselbe Weg wie in der Mail-Leiste
     * (`onNavigiertZuSeite`).
     */
    fensterId?: string | null;
};

const sectionTitle: React.CSSProperties = {
    fontWeight: 600,
    fontSize: 14,
    color: "#2d142b",
    marginBottom: 8,
};

export const TerminSidecar: React.FC<Props> = ({
    mailbox,
    eventId,
    graphReadOnly = false,
    kmvsReadOnly: kmvsReadOnlyProp = false,
    teilnehmerKontext,
    fensterId = null,
}) => {
    const navigate = useNavigate();
    const { openWindow, minimizeWindow } = useWindowManager();
    const [notes, setNotes] = useState<CalendarTerminNoteDto[]>([]);
    /** P10 — die mit diesem Termin verknüpften Mails (Snapshot). */
    const [mails, setMails] = useState<TerminMailDto[]>([]);
    const [mailBusy, setMailBusy] = useState<number | null>(null);
    const [terminId, setTerminId] = useState<number | null>(null);
    const [docs, setDocs] = useState<DocumentDto[]>([]);
    // K10: NUR noch der Titel des aktuell verknüpften Vorgangs — die
    // vollständige Vorgangsliste (`getAllVorgaenge`) wird nicht mehr geladen.
    const [vorgangTitel, setVorgangTitel] = useState<string | null>(null);
    const [vorgangId, setVorgangId] = useState<number | null>(null);
    /**
     * Ist der verknüpfte Vorgang für diese Nutzerin LESBAR? Entscheidet über
     * den Sprung: `getVorgang` scheitert beim persönlichen Vorgang einer
     * Kollegin (oder einem gelöschten) — dann bleibt die Zeile stehen, aber
     * ohne Link, statt auf eine leere Detailseite zu führen.
     */
    const [vorgangLesbar, setVorgangLesbar] = useState(false);
    const [uploading, setUploading] = useState(false);
    // K3: Fehler beim Verknüpfen erscheinen inline statt nur in der Konsole.
    const [vorgangFehler, setVorgangFehler] = useState<string | null>(null);
    // „Vorgang verknüpfen“ als Popup über dem Termin-Fenster (User 27.10.2026).
    const [verknuepfenOffen, setVerknuepfenOffen] = useState(false);
    // Kunde des Termins — steuert im Picker nur die Rangstufe 1, nie die Sicht.
    const [kundeId, setKundeId] = useState<number | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    /**
     * PO 28.09.2026 — die Nutzerin sieht den Termin nur als FO/BO eines
     * beteiligten Kontakts (Kalender-Ebene „Termine meiner Kontakte"). Dann
     * sind Notizen und Notiz-Seite schreibbar (das Backend erlaubt es auf
     * diesem Weg ausdrücklich), der Vorgangs-Block bleibt gesperrt (Verknüpfen
     * braucht Kalender-Leserecht) und Dokumente bleiben an `graphReadOnly`.
     */
    const [nurKontaktZugang, setNurKontaktZugang] = useState(false);
    // Über den Vorgang sichtbar (PO 28.09.2026): nur lesen — auch Notizen
    // bleiben zu, denn `notizenGesperrt` öffnet sie nur dem Kontakt-Weg.
    const [nurVorgangZugang, setNurVorgangZugang] = useState(false);
    const kmvsReadOnly = kmvsReadOnlyProp || nurKontaktZugang || nurVorgangZugang;
    const notizenGesperrt = graphReadOnly && !nurKontaktZugang;

    const loadNotes = useCallback(() => {
        calendarTerminService.listNotes(mailbox, eventId).then(setNotes).catch((e) => console.error(e));
    }, [mailbox, eventId]);

    /**
     * P10 — verknüpfte Mails laden. Eigener Ladeweg neben Notizen und
     * Dokumenten, damit ein Fehler hier die übrigen Blöcke nicht mitreißt; ohne
     * Sidecar antwortet der Server schlicht mit einer leeren Liste.
     */
    const loadMails = useCallback(() => {
        calendarTerminService
            .listMailsForTermin(mailbox, eventId)
            .then(setMails)
            .catch((e) => {
                console.error("[TerminSidecar] Verknüpfte Mails laden fehlgeschlagen", e);
                setMails([]);
            });
    }, [mailbox, eventId]);

    // Reiner Lese-Aufruf: legt KEINEN Sidecar an. Erst eine Notiz/Verknüpfung/
    // Datei erzeugt ihn. Ohne Sidecar (terminId null) gibt es noch keine Doks.
    const loadMetaAndDocs = useCallback(() => {
        calendarTerminService
            .getMeta(mailbox, eventId)
            .then((meta) => {
                setVorgangId(meta.vorgangId);
                setKundeId(meta.kundeId ?? null);
                setTerminId(meta.terminId);
                setNurKontaktZugang(meta.zugang === "kontakt");
                setNurVorgangZugang(meta.zugang === "vorgang");
                if (meta.terminId == null) {
                    setDocs([]);
                    return;
                }
                documentService
                    .list({ calendarTerminId: meta.terminId })
                    .then(setDocs)
                    .catch((e) => console.error(e));
            })
            .catch((e) => console.error(e));
    }, [mailbox, eventId]);

    useEffect(() => {
        loadNotes();
        loadMetaAndDocs();
        loadMails();
    }, [loadNotes, loadMetaAndDocs, loadMails]);

    // K10: Der Titel des verknüpften Vorgangs wird gezielt nachgeladen. Der
    // frühere `focus`-Reload der GANZEN Vorgangsliste entfällt ersatzlos — der
    // Picker sucht ohnehin serverseitig und sieht neue Vorgänge sofort.
    useEffect(() => {
        if (vorgangId == null) {
            setVorgangTitel(null);
            setVorgangLesbar(false);
            return;
        }
        let abgebrochen = false;
        vorgangService
            .getVorgang(vorgangId)
            .then((v) => {
                if (abgebrochen) return;
                setVorgangTitel(v?.titel ?? null);
                // Antwortet der Server, ist der Vorgang lesbar — auch ohne
                // Titel; der Link heißt dann „Vorgang #123".
                setVorgangLesbar(v != null);
            })
            .catch(() => {
                // Kein Leserecht oder gelöscht → nur die Nummer anzeigen.
                if (abgebrochen) return;
                setVorgangTitel(null);
                setVorgangLesbar(false);
            });
        return () => {
            abgebrochen = true;
        };
    }, [vorgangId]);

    const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            // Schreibender Upload: Sidecar anlegen/holen und terminId auflösen.
            const meta = await calendarTerminService.ensureMeta(mailbox, eventId);
            if (meta.terminId == null) return;
            await documentService.upload({ file, calendarTerminId: meta.terminId });
            const list = await documentService.list({ calendarTerminId: meta.terminId });
            setDocs(list);
        } catch (err) {
            console.error("Upload failed", err);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const removeDoc = async (id: number) => {
        try {
            await documentService.remove(id);
            loadMetaAndDocs();
        } catch (err) {
            console.error("Remove failed", err);
        }
    };

    // Download über signierte URL (Bearer-only-Vorbereitung): erst authentifiziert
    // die HMAC-signierte URL holen, dann öffnen — kein Cookie/Bearer in der Navigation.
    const downloadDoc = async (id: number) => {
        try {
            await documentService.openSignedDownload(id);
        } catch (err) {
            console.error("Download failed", err);
        }
    };

    /**
     * Nur noch fürs LÖSEN („entfernen"). Verknüpft wird seit dem 27.10.2026 im
     * Popup „Vorgang verknüpfen" (`VorgangZuweisenDialog`), derselben Auswahl
     * wie „Bestehendem Vorgang zuordnen" — dort ist bei einem Vorgang mit
     * To-dos wieder ein To-do zu wählen. Mit dem Vorgang fällt auch ein
     * To-do-Bezug.
     */
    const onSelectVorgang = async (next: number | null) => {
        setVorgangFehler(null);
        try {
            await calendarTerminService.setVorgang(mailbox, eventId, next);
            // Ein früherer To-do-Bezug fällt mit: er gehörte zum alten Vorgang.
            await calendarTerminService.setTodo(mailbox, eventId, null);
            setVorgangId(next);
        } catch (err: any) {
            // K3: sichtbar melden. Der häufigste Fall ist ein 403 auf den
            // ZIEL-Vorgang (persönlicher Vorgang einer Kollegin) — den soll der
            // Nutzer lesen können, nicht nur die Browser-Konsole.
            console.error("Vorgang-Verknüpfung fehlgeschlagen", err);
            setVorgangFehler(
                typeof err?.message === "string" && err.message.includes("403")
                    ? "Kein Zugriff auf den Ziel-Vorgang."
                    : "Verknüpfung konnte nicht gespeichert werden.",
            );
        }
    };

    // SP8/A17: „Neuen Vorgang erstellen" läuft über die Kontakt-Ableitung aus
    // den Teilnehmer-E-Mails (NeuerVorgangAusTerminDialog) und bindet das
    // Ergebnis automatisch an den Termin (Auto-Bind statt manueller Dropdown-
    // Auswahl wie zuvor).
    const [vorgangKtx, setVorgangKtx] = useState<TerminVorgangKontext | null>(null);
    const createNewVorgang = () => {
        if (kmvsReadOnly) return;
        setVorgangKtx({
            mailbox,
            eventId,
            attendees: teilnehmerKontext?.attendees,
            organizer: teilnehmerKontext?.organizer,
        });
    };

    // Nach dem Auto-Bind die Verknüpfungs-Anzeige nachziehen (SP8-Testpunkt:
    // „nach Anlage ist der Vorgang am Termin verknüpft — Sidecar zeigt ihn").
    const onVorgangGebunden = useCallback((result: VorgangCreateResult) => {
        // K4/E06: gebunden wird IMMER der Vorgang — bei „bestehendem Vorgang"
        // der Eltern-Vorgang, nicht das dabei entstandene Todo.
        const zielVorgang = result.vorgangId ?? result.parentVorgangId ?? null;
        if (zielVorgang != null) setVorgangId(zielVorgang);
    }, []);

    // Notiz-Seite im OneNote-Stil öffnen (SP7/A12): Route trägt die Sidecar-
    // terminId; existiert noch kein Sidecar, legt ensureMeta ihn an (nur WRITE —
    // read-only ohne Sidecar hat schlicht noch keine Notizen).
    const openNotizSeite = async () => {
        try {
            let id = terminId;
            if (id == null && !notizenGesperrt) {
                const meta = await calendarTerminService.ensureMeta(mailbox, eventId);
                id = meta.terminId;
                setTerminId(id);
            }
            if (id == null) {
                void hinweisen({ titel: "Noch keine Notizen", text: "Für diesen Termin gibt es noch keine Notizen." });
                return;
            }
            // Wie beim Sprung zum Vorgang: das Fenster ins Dock, sonst läge es
            // über der Notiz-Seite.
            if (fensterId) minimizeWindow(fensterId);
            navigate(`/termine/notizen/${id}`);
        } catch (err) {
            console.error("Notiz-Seite öffnen fehlgeschlagen", err);
        }
    };

    /**
     * Sprung auf den verknüpften Vorgang (PO 24.08.2026). Die Verknüpfung war
     * eine Sackgasse: sie stand da, ließ sich ändern und lösen — nur nicht
     * öffnen. Der Weg ist derselbe wie im Peek-Popover (K9) und in der
     * Mail-Leiste: Fenster ins Dock, dann auf die Vorgangs-Seite.
     */
    const oeffneVorgang = () => {
        if (vorgangId == null || !vorgangLesbar) return;
        if (fensterId) minimizeWindow(fensterId);
        navigate(`/vorgaenge/${vorgangId}`);
    };

    /**
     * P10 — eine verknüpfte Mail öffnen. Adressiert wird über
     * (mailbox, messageId) aus dem Snapshot; Betreff und Text lädt das
     * Detailfenster selbst nach — derselbe Weg wie im Schriftwechsel des
     * Vorgangs und bei den Todo-Mails.
     */
    const oeffneMail = (m: TerminMailDto) => {
        openMailDetailWindow(openWindow, {
            email: emailAusTerminMail(m),
            mailbox: m.mailbox,
            singleMessage: true,
        });
    };

    /**
     * Der Rückweg: EINE Mail wieder vom Termin lösen. Löst ausschließlich die
     * Termin-Verknüpfung — ein Vorgangs-, Todo- oder Kontakt-Link derselben
     * Mail bleibt bestehen (das sichert der Endpunkt serverseitig ab). Wichtig
     * nach einer Vorgangs-Übernahme (P9), die bewusst ALLES mitbringt: einzelne
     * Ausreißer müssen wieder herausgehen können.
     */
    const loeseMail = async (linkId: number) => {
        setMailBusy(linkId);
        try {
            await calendarTerminService.unlinkMailFromTermin(linkId);
            setMails((liste) => liste.filter((m) => m.id !== linkId));
        } catch (err) {
            console.error("[TerminSidecar] Mail-Verknüpfung lösen fehlgeschlagen", err);
            // Die Liste könnte auseinandergelaufen sein — lieber neu holen.
            loadMails();
        } finally {
            setMailBusy(null);
        }
    };

    const verknuepftZeile: React.CSSProperties = {
        display: "flex",
        alignItems: "baseline",
        gap: 10,
        padding: "8px 10px",
        border: "1px solid #e3dce6",
        borderRadius: 6,
        background: "#faf8fb",
    };
    const verknuepftName: React.CSSProperties = {
        flex: 1,
        minWidth: 0,
        fontSize: 13,
        fontWeight: 600,
        color: "#2d142b",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    };
    /** Der Titel als Sprung auf den Vorgang — Optik der Mail-Zeilen darunter. */
    const verknuepftLink: React.CSSProperties = {
        ...verknuepftName,
        border: "none",
        background: "transparent",
        padding: 0,
        textAlign: "left",
        font: "inherit",
        fontSize: 13,
        fontWeight: 600,
        color: "#4d2051",
        textDecoration: "underline",
        textUnderlineOffset: 2,
        cursor: "pointer",
    };
    const miniLink: React.CSSProperties = {
        border: "none",
        background: "none",
        padding: 0,
        color: "#4d2051",
        font: "inherit",
        fontSize: 12,
        textDecoration: "underline",
        textUnderlineOffset: 2,
        cursor: "pointer",
    };
    const linkBtnStyle: React.CSSProperties = {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "7px 12px",
        border: "1px dashed #4d2051",
        borderRadius: 6,
        backgroundColor: "#ffffff",
        color: "#4d2051",
        cursor: kmvsReadOnly ? "not-allowed" : "pointer",
        fontSize: 12,
        fontWeight: 600,
        fontFamily: "inherit",
        opacity: kmvsReadOnly ? 0.5 : 1,
        marginTop: 8,
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Vorgang-Verknüpfung */}
            <div>
                <div style={sectionTitle}>Verknüpfter Vorgang</div>
                {/* Ist ein Vorgang verknüpft, steht er als Zeile da — mit
                    „ändern" und „entfernen". „Vorgang verknüpfen…" und
                    „ändern" öffnen das Popup „Vorgang verknüpfen" (User
                    27.10.2026: „als Popup über dem normalen Popup, nicht
                    dahinter"); vorher klappte die Auswahl im Fenster auf. */}
                {vorgangId != null ? (
                    <div style={verknuepftZeile}>
                        {/* PO 24.08.2026: der Titel führt auf den Vorgang.
                            Ohne Leserecht bleibt es Text — ein Link dorthin
                            endete auf einer leeren Detailseite. */}
                        {vorgangLesbar ? (
                            <button
                                type="button"
                                style={verknuepftLink}
                                onClick={oeffneVorgang}
                                title={`${vorgangTitel ?? `Vorgang #${vorgangId}`} — Vorgang öffnen`}
                            >
                                {vorgangTitel ?? `Vorgang #${vorgangId}`}
                            </button>
                        ) : (
                            <span
                                style={verknuepftName}
                                title="Für diesen Vorgang fehlt das Leserecht."
                            >
                                {`Vorgang #${vorgangId}`}
                            </span>
                        )}
                        {!kmvsReadOnly && (
                            <>
                                <button
                                    type="button"
                                    style={miniLink}
                                    onClick={() => setVerknuepfenOffen(true)}
                                >
                                    ändern
                                </button>
                                <button
                                    type="button"
                                    style={miniLink}
                                    onClick={() => void onSelectVorgang(null)}
                                >
                                    entfernen
                                </button>
                            </>
                        )}
                    </div>
                ) : kmvsReadOnly ? (
                    <div style={{ fontSize: 13, color: "#6f6c75" }}>Keine Verknüpfung.</div>
                ) : (
                    <button
                        type="button"
                        style={linkBtnStyle}
                        onClick={() => setVerknuepfenOffen(true)}
                    >
                        Vorgang verknüpfen…
                    </button>
                )}
                <button
                    type="button"
                    style={{ ...linkBtnStyle, marginLeft: vorgangId == null && !kmvsReadOnly ? 8 : 0 }}
                    disabled={kmvsReadOnly}
                    onClick={createNewVorgang}
                >
                    + Neuen Vorgang erstellen
                </button>
                {vorgangFehler && (
                    <div style={{ marginTop: 6, color: "#c23b3b", fontSize: 12 }} role="alert">
                        {vorgangFehler}
                    </div>
                )}
            </div>

            {/* P10 — Verknüpfte Mails. Steht direkt unter dem Vorgang, weil die
                Post meist von dort kommt: wird aus einem Vorgang heraus ein
                Termin verknüpft, erbt der Termin dessen Mails (P9). Anzeige aus
                dem Snapshot, ohne Graph-Abruf. */}
            <div>
                <div style={sectionTitle}>Verknüpfte Mails</div>
                {mails.length === 0 ? (
                    <div style={{ color: "#6f6c75", fontStyle: "italic", fontSize: 13 }}>
                        Keine Mails verknüpft.
                    </div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {mails.map((m) => {
                            const z = terminMailZeile(m);
                            return (
                                <div
                                    key={z.key}
                                    style={{
                                        display: "flex",
                                        alignItems: "baseline",
                                        gap: 10,
                                        padding: "8px 10px",
                                        backgroundColor: "#f7f5fa",
                                        borderRadius: 6,
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={() => oeffneMail(m)}
                                        title={`${z.betreff} — ${z.absender}`}
                                        style={{
                                            flex: 1,
                                            minWidth: 0,
                                            border: "none",
                                            background: "transparent",
                                            padding: 0,
                                            font: "inherit",
                                            textAlign: "left",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <span
                                            style={{
                                                display: "block",
                                                fontSize: 13,
                                                fontWeight: 600,
                                                color: "#4d2051",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {z.betreff}
                                        </span>
                                        <span
                                            style={{
                                                display: "block",
                                                fontSize: 12,
                                                color: "#6f6c75",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {z.absender}
                                            {z.datum ? ` · ${z.datum}` : ""}
                                        </span>
                                    </button>
                                    {/* E10: das Lösen fasst nur KMVS-Metadaten an —
                                        derselbe Maßstab wie beim Vorgangs-Block. */}
                                    {!kmvsReadOnly && (
                                        <button
                                            type="button"
                                            style={miniLink}
                                            disabled={mailBusy === z.linkId}
                                            title="Diese Mail vom Termin lösen (die Mail selbst bleibt)"
                                            onClick={() => void loeseMail(z.linkId)}
                                        >
                                            lösen
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Notizen — PO 28.09.2026: kein Notizfeld mehr, sondern EIN Knopf
                zur Notiz-Seite. Die Notiz-Seite ist das, was die Vorgangsansicht
                im Verlauf zeigt (by-vorgang → `notizSeite`); die früheren
                Kurz-Notizen tauchten dort nie auf. Vorhandene stehen weiter
                lesbar darunter. */}
            <div>
                <div style={sectionTitle}>Notizen</div>
                <button
                    type="button"
                    onClick={() => void openNotizSeite()}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "9px 16px",
                        border: "none",
                        borderRadius: 8,
                        backgroundColor: "#4d2051",
                        color: "#ffffff",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 600,
                        fontFamily: "inherit",
                    }}
                >
                    <Notepad16Regular aria-hidden />
                    Notiz
                </button>
                {notes.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
                        <div style={{ fontSize: 12, color: "#6f6c75" }}>Frühere Kurz-Notizen</div>
                        {notes.map((n) => (
                            <div
                                key={n.id}
                                style={{
                                    padding: "8px 10px",
                                    backgroundColor: "#f7f5fa",
                                    borderRadius: 6,
                                    fontSize: 13,
                                    color: "#2d142b",
                                }}
                            >
                                {n.text}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Dokumente */}
            <div>
                <div style={sectionTitle}>Dokumente</div>
                {docs.length === 0 ? (
                    <div style={{ color: "#6f6c75", fontStyle: "italic", fontSize: 13, marginBottom: 12 }}>
                        Keine Dokumente.
                    </div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                        {docs.map((d) => (
                            <div key={d.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px", backgroundColor: "#f7f5fa", borderRadius: 6, fontSize: 13 }}>
                                <button type="button" onClick={() => downloadDoc(d.id)} style={{ border: "none", background: "transparent", padding: 0, font: "inherit", textAlign: "left", cursor: "pointer", color: "#4d2051", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {d.fileName}
                                </button>
                                {!graphReadOnly && (
                                    <button type="button" onClick={() => removeDoc(d.id)} style={{ border: "none", background: "transparent", color: "#b91c1c", cursor: "pointer", fontSize: 12 }}>
                                        Löschen
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                {!graphReadOnly && (
                    <>
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", border: "1px dashed #4d2051", borderRadius: 6, backgroundColor: "#ffffff", color: "#4d2051", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}
                        >
                            {uploading ? "Lade hoch…" : "+ Datei hochladen"}
                        </button>
                        <input ref={fileInputRef} type="file" style={{ display: "none" }} onChange={handleFile} />
                    </>
                )}
            </div>

            {/* SP8/A17: Kontakt-Ableitung + Auto-Bind (Portal, liegt über dem Fenster). */}
            <NeuerVorgangAusTerminDialog
                kontext={vorgangKtx}
                onClose={() => setVorgangKtx(null)}
                onGebunden={onVorgangGebunden}
            />
            <VorgangZuweisenDialog
                kontext={verknuepfenOffen && !kmvsReadOnly ? { mailbox, eventId, contactId: kundeId } : null}
                onClose={() => setVerknuepfenOffen(false)}
                onGebunden={(neueVorgangId) => {
                    setVorgangFehler(null);
                    setVorgangId(neueVorgangId);
                }}
            />
        </div>
    );
};

export default TerminSidecar;
